import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

import {
  LayoutDashboard,
  Image,
  Sparkles,
  Camera,
  Crown,
  Receipt,
  Newspaper,
  HelpCircle,
  MessageSquareQuote,
  ScrollText,
  Store,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

// ---------------------------------------------------------------------------
// Navigation items
// ---------------------------------------------------------------------------
const NAV_ITEMS = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/brand", icon: Store, label: "Brand" },
  { to: "/admin/images", icon: Image, label: "Image Library" },
  { to: "/admin/services", icon: Sparkles, label: "Services" },
  { to: "/admin/portfolio", icon: Camera, label: "Portfolio" },
  { to: "/admin/packages", icon: Crown, label: "Packages" },
  { to: "/admin/pricelist", icon: Receipt, label: "Price List" },
  { to: "/admin/press", icon: Newspaper, label: "Press" },
  { to: "/admin/faqs", icon: HelpCircle, label: "FAQ" },
  { to: "/admin/testimonials", icon: MessageSquareQuote, label: "Testimonials" },
  { to: "/admin/certificates", icon: ScrollText, label: "Certificates" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------
function Sidebar({ open, onClose }) {
  const location = useLocation();

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-full w-64 bg-[#0a0908] border-r border-[#d4af37]/15 flex flex-col transition-transform duration-300 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Brand */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-[#d4af37]/10 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/25 flex items-center justify-center">
              <span className="text-[#d4af37] text-xs font-bold">AR</span>
            </div>
            <div className="leading-tight">
              <p className="text-sm text-[#fdfbf7] font-medium">Adibah</p>
              <p className="text-[10px] text-[#a6a196] tracking-wider uppercase">
                Admin Panel
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-[#a6a196] hover:text-[#fdfbf7] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav items */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {NAV_ITEMS.map((item) => {
              const isActive = item.end
                ? location.pathname === item.to
                : location.pathname.startsWith(item.to);
              const Icon = item.icon;

              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onClose}
                  className={cn(
                    "flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-all duration-200 group",
                    isActive
                      ? "bg-[#d4af37]/10 text-[#d4af37] border border-[#d4af37]/20"
                      : "text-[#a6a196] hover:text-[#fdfbf7] hover:bg-[#15120f] border border-transparent"
                  )}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span>{item.label}</span>
                  {isActive && (
                    <ChevronRight className="w-3.5 h-3.5 ml-auto text-[#d4af37]" />
                  )}
                </NavLink>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[#d4af37]/10 shrink-0">
          <p className="text-[10px] text-[#a6a196]/60 text-center tracking-wider uppercase">
            Sanggar Rias Adibah &copy; {new Date().getFullYear()}
          </p>
        </div>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// Top bar
// ---------------------------------------------------------------------------
function TopBar({ onMenuClick }) {
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Berhasil keluar");
    } catch {
      toast.error("Gagal keluar");
    }
  };

  const initials = user?.displayName
    ? user.displayName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2)
    : "A";

  return (
    <header className="sticky top-0 z-30 h-16 border-b border-[#d4af37]/10 bg-[#0a0908]/95 backdrop-blur-sm flex items-center justify-between px-4 sm:px-6 shrink-0">
      <button
        onClick={onMenuClick}
        className="lg:hidden text-[#a6a196] hover:text-[#fdfbf7] transition-colors"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="hidden lg:block" />

      <div className="flex items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-3 outline-none group">
              <Avatar className="w-8 h-8 border border-[#d4af37]/20">
                <AvatarImage src={user?.photoURL} alt={user?.displayName} />
                <AvatarFallback className="bg-[#15120f] text-[#d4af37] text-xs">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left">
                <p className="text-sm text-[#fdfbf7] leading-tight group-hover:text-[#d4af37] transition-colors">
                  {user?.displayName || "Admin"}
                </p>
                <p className="text-[11px] text-[#a6a196] leading-tight">
                  {user?.email}
                </p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-56 bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]"
          >
            <DropdownMenuLabel className="text-[#a6a196] font-normal text-xs">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#d4af37]/10" />
            <DropdownMenuItem
              onClick={handleLogout}
              className="text-red-400 hover:text-red-300 hover:bg-red-400/10 focus:text-red-300 focus:bg-red-400/10 cursor-pointer"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Keluar
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// Main layout
// ---------------------------------------------------------------------------
export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0908] flex">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col lg:ml-64">
        <TopBar onMenuClick={() => setSidebarOpen(true)} />

        <main className="flex-1 bg-[#15120f] p-6 sm:p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
